/**
 * Picking a sailing for a cruise sticker sheet.
 *
 * A sheet is not one design but a set: a sticker for the ship, one for every
 * port, the line-and-year badge, and a filler for anything left over. The
 * backend works all of that out — this asks it what a sailing earns, shows the
 * customer the sheets, and puts one line in the cart per sheet.
 *
 * One line per sheet, rather than one line with a quantity, because each sheet
 * is a different print file and a line item carries exactly one. That also
 * means the order pipeline needs no special case: every line already maps to a
 * Printful item with its own file.
 *
 * Two things this must not do, both of which end as a sheet somebody paid for
 * and cannot be printed:
 *
 *   Offer a sailing whose ports have no badges. The backend reports that as
 *   `sellable: false` with the list, and the button stays disabled.
 *
 *   Add to the cart before the print files exist. Each URL is fetched here
 *   first, which renders it and leaves it in the backend's cache, so Printful's
 *   own fetch minutes later is served from there rather than from a machine
 *   that happens to be busy.
 */

/** How long to wait for a sheet to render before giving up on it. */
const SHEET_RENDER_TIMEOUT_MS = 30000;

/**
 * How big the composed sheet is drawn.
 *
 * Square, because the gallery slots are: the slide keeps the aspect ratio of
 * the product image it replaces, so a portrait canvas is letterboxed into a
 * square anyway, with the white band above and below reading as part of the
 * picture. Drawing the square ourselves means we decide what goes in that band.
 */
const PREVIEW_SIDE_PX = 1400;

/**
 * The printed sheet, in fractions of itself.
 *
 * 5.83in x 8.27in with six 2.25in stickers in a 2 x 3 grid and a 0.25in
 * gutter, centred — the same arithmetic `sticker-sheet-layout.js` does for the
 * print file, expressed as ratios so it survives any size we draw at. The
 * gutter is a fraction of the width and used on both axes, which is right
 * because the page keeps its aspect ratio: an inch is an inch either way.
 */
const PAGE = {
  aspect: 1750 / 2482,
  marginX: 0.093,
  marginY: 0.062,
  gutter: 0.043,
  /** How much of the square the page fills, leaving room to read as an object. */
  fill: 0.94,
};

/**
 * How opaque a pixel must be to count as part of the drawing, and how finely
 * to look. The backend's `sticker-sheet-trim.js` uses the same two numbers,
 * and has the long version of why they are these ones.
 */
const ALPHA_THRESHOLD = 16;
const SAMPLE_PX = 256;

/** The whole image, for when there is nothing to trim or no way to look. */
const FULL_BOX = { x: 0, y: 0, width: 1, height: 1 };

/**
 * Where the drawing actually is inside its square, as fractions of it.
 *
 * A badge is drawn with room around it, and how much room is a property of the
 * drawing: a port badge fills 69% of its width, the "Bon Voyage" filler 40%.
 * The print file measures each badge and scales that room out so every sticker
 * is 2.25in on its longest side, and this preview has to do the same or it is
 * showing the customer a sheet we no longer print.
 *
 * Read off the alpha channel, which is the same channel the cutter follows.
 * This is why the previews are the transparent PNGs rather than the lighter
 * flattened twins: a `.jpg` has no alpha to measure, and the white outline
 * around a badge would be indistinguishable from the white around it.
 */
function contentBoxOf(image) {
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  if (!sourceWidth || !sourceHeight) return FULL_BOX;

  const scale = Math.min(1, SAMPLE_PX / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(image, 0, 0, width, height);

  let pixels;
  try {
    pixels = ctx.getImageData(0, 0, width, height).data;
  } catch (err) {
    // A canvas tainted by an image fetched without CORS. The badge is drawn
    // the way it was before any of this: smaller than it prints, which is
    // better than not drawn at all.
    console.warn("[sticker-sheet] could not measure a badge.", err);
    return FULL_BOX;
  }

  let left = width;
  let right = -1;
  let top = height;
  let bottom = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (pixels[(y * width + x) * 4 + 3] < ALPHA_THRESHOLD) continue;
      if (x < left) left = x;
      if (x > right) right = x;
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }

  if (right < left || bottom < top) return FULL_BOX;

  return {
    x: left / width,
    y: top / height,
    // The far edge is the far side of that pixel, not its near side.
    width: (right + 1 - left) / width,
    height: (bottom + 1 - top) / height,
  };
}

class VariantPickerStickerSheet extends HTMLElement {
  constructor() {
    super();
    this.shipSelect = this.querySelector("#sheet-ship-select");
    this.saildateSelect = this.querySelector("#sheet-saildate-select");
    this.status = this.querySelector("#sheet-status");
    this.formId = this.shipSelect.getAttribute("form");

    this.plan = null;

    this.shipSelect.addEventListener("change", () => this.handleShipChange());
    this.saildateSelect.addEventListener("change", () =>
      this.handleSaildateChange()
    );
  }

  #fetchWithConfig(url, options = {}) {
    return fetch(url, {
      credentials: "same-origin",
      mode: "cors",
      ...options,
      headers: {
        "Content-Type": "application/json",
        "x-api-key": X_API_KEY,
        ...(options.headers || {}),
      },
    });
  }

  async connectedCallback() {
    await this.waitForForm();
    this.submitButton = this.productForm.querySelector('[type="submit"]');
    this.productForm.addEventListener("submit", (event) =>
      this.handleSubmit(event)
    );
    this.setBuyable(false, "Choose your ship and sail date");
    await this.fetchShips();
  }

  waitForForm() {
    return new Promise((resolve) => {
      const check = () => {
        this.productForm = document.getElementById(this.formId);
        this.productForm ? resolve() : requestAnimationFrame(check);
      };
      check();
    });
  }

  /** The one place the button's state and the reason for it are set together. */
  setBuyable(buyable, message = "") {
    if (this.submitButton) this.submitButton.disabled = !buyable;
    this.status.textContent = message;
    this.status.classList.toggle("sheet-status--problem", !buyable && !!message);
  }

  async fetchShips() {
    try {
      const response = await this.#fetchWithConfig(`${EXT_API_BASE_URL}/ships`);
      if (!response.ok) throw new Error("Failed to fetch ships");
      const { data: ships } = await response.json();

      this.shipSelect.innerHTML = '<option value="">Select a ship</option>';
      for (const ship of ships) {
        const option = document.createElement("option");
        option.value = ship.slug;
        option.textContent = ship.name;
        this.shipSelect.appendChild(option);
      }
    } catch (err) {
      console.error("[sticker-sheet] could not load ships.", err);
      this.setBuyable(false, "We could not load the ship list. Please reload.");
    }
  }

  async handleShipChange() {
    this.plan = null;
    this.#restoreGallery();
    this.saildateSelect.innerHTML = '<option value="">Select a saildate</option>';
    this.saildateSelect.disabled = true;
    this.setBuyable(false, "Choose your sail date");

    if (!this.shipSelect.value) return;

    try {
      const response = await this.#fetchWithConfig(
        `${EXT_API_BASE_URL}/itineraries/ship/${this.shipSelect.value}`
      );
      if (!response.ok) throw new Error("Failed to fetch saildates");
      const { data: saildates } = await response.json();

      for (const date of saildates) {
        const option = document.createElement("option");
        option.value = date.id;
        option.textContent = date.pretty_saildate;
        this.saildateSelect.appendChild(option);
      }
      this.saildateSelect.disabled = false;
    } catch (err) {
      console.error("[sticker-sheet] could not load sail dates.", err);
      this.setBuyable(false, "We could not load sail dates for that ship.");
    }
  }

  async handleSaildateChange() {
    this.plan = null;
    this.#restoreGallery();

    if (!this.saildateSelect.value) {
      this.setBuyable(false, "Choose your sail date");
      return;
    }

    this.setBuyable(false, "Working out your stickers…");

    try {
      const response = await this.#fetchWithConfig(
        `${EXT_API_BASE_URL}/sticker-sheets/${encodeURIComponent(
          this.saildateSelect.value
        )}`
      );
      if (!response.ok) throw new Error(`Plan request failed: ${response.status}`);
      const { data: plan } = await response.json();

      /*
       * A sailing calling at a port nobody has drawn cannot be made, and
       * saying so here is the whole point of asking before the sale: the
       * alternative is a blank square on a printed sheet.
       */
      if (!plan.sellable) {
        this.setBuyable(
          false,
          "We don't have badges for every port on this sailing yet. " +
            "Try another date, or email us and we'll draw them."
        );
        return;
      }

      this.plan = plan;
      this.renderPreview(plan);
      this.setBuyable(
        true,
        plan.sheetCount === 1
          ? "One sheet, six stickers."
          : `${plan.sheetCount} sheets, ${plan.sheetCount * 6} stickers.`
      );
    } catch (err) {
      console.error("[sticker-sheet] could not plan the sheet.", err);
      this.setBuyable(false, "We could not build that sheet. Please try again.");
    }
  }

  /**
   * Put the sheet and its stickers in the product gallery.
   *
   * The sheet first, then every sticker on it, which is how a customer reads a
   * product: the thing, then the detail. The gallery slides are Shopify media
   * the product already carries; this swaps their sources, the same way the
   * ship-and-sail-date picker swaps in a rendered design.
   *
   * The sheet image is drawn here on a canvas rather than fetched. The badges
   * are already loaded and the layout is arithmetic, so composing it costs
   * nothing — where asking the backend would queue a Puppeteer render for
   * somebody who has not bought anything, and hand back a transparent PNG that
   * looks like nothing on a white page.
   */
  async renderPreview(plan) {
    const slides = this.#gallerySlots();
    if (slides.length === 0) return;

    this.#rememberGallery(slides);

    const sheets = await Promise.all(
      plan.sheets.map((sheet) => this.#composeSheet(sheet, plan))
    );

    // Sheets, then the stickers from each in order.
    const images = [
      ...sheets.map((src, i) => ({
        src,
        label:
          plan.sheetCount > 1 ? `Sheet ${i + 1} of ${plan.sheetCount}` : "Your sticker sheet",
      })),
      ...plan.sheets.flatMap((sheet) =>
        sheet.stickers.map((sticker) => ({
          src: sticker.preview,
          label: sticker.label,
        }))
      ),
    ];

    if (images.length > slides.length) {
      console.warn(
        `[sticker-sheet] this sailing has ${images.length} images but the gallery has ${slides.length} slides. ` +
          `Add product images so every sticker has one.`
      );
    }

    slides.forEach((slot, index) => {
      const image = images[index];
      // A slide with nothing to show would otherwise keep the placeholder the
      // product was set up with, which looks like somebody else's sticker.
      slot.show(Boolean(image));
      if (!image) return;
      for (const node of slot.images) {
        node.src = image.src;
        node.removeAttribute("srcset");
        node.removeAttribute("sizes");
        node.alt = image.label;
      }
    });
  }

  /**
   * Put the product's own images back.
   *
   * Changing ship or sail date leaves the gallery showing the last sailing's
   * stickers, which is worse than showing nothing: it is somebody else's trip,
   * presented as yours. Captured once, before the first swap.
   */
  #restoreGallery() {
    if (!this.originalGallery) return;
    for (const { slot, images } of this.originalGallery) {
      slot.show(true);
      images.forEach(({ node, src, srcset, sizes, alt }) => {
        node.src = src;
        if (srcset) node.setAttribute("srcset", srcset);
        if (sizes) node.setAttribute("sizes", sizes);
        node.alt = alt;
      });
    }
  }

  #rememberGallery(slots) {
    if (this.originalGallery) return;
    this.originalGallery = slots.map((slot) => ({
      slot,
      images: slot.images.map((node) => ({
        node,
        src: node.src,
        srcset: node.getAttribute("srcset"),
        sizes: node.getAttribute("sizes"),
        alt: node.alt,
      })),
    }));
  }

  /**
   * The gallery slides, with their modal twins.
   *
   * Each Shopify media appears twice — once in the slider, once in the
   * lightbox — and both have to be swapped or opening the lightbox shows the
   * placeholder the slide no longer displays.
   */
  #gallerySlots() {
    return [...document.querySelectorAll(".product__media-list li[data-media-id]")].map(
      (slide) => {
        const mediaId = slide.dataset.mediaId.split("-").pop();
        const modalImage = document.querySelector(
          `.product-media-modal__content img[data-media-id="${mediaId}"]`
        );

        return {
          images: [slide.querySelector("img"), modalImage].filter(Boolean),
          show(visible) {
            slide.style.display = visible ? "" : "none";
            const modalSlide = modalImage?.closest("[data-media-id]");
            if (modalSlide) modalSlide.style.display = visible ? "" : "none";
          },
        };
      }
    );
  }

  /**
   * One sheet, drawn as the page it arrives as.
   *
   * A square canvas with the sheet laid on it: white, edged, and shadowed, so
   * what the customer sees is a piece of paper with their stickers on it
   * rather than six badges floating in a void. The proportions and the grid
   * are the print file's, so the preview is a promise about the thing in the
   * envelope.
   *
   * Drawn here rather than fetched. The badges are already loaded and the
   * layout is arithmetic, so composing it costs nothing — where asking the
   * backend would queue a Puppeteer render for somebody who has not bought
   * anything, and hand back a transparent PNG that looks like nothing on a
   * white page.
   *
   * The badges are requested with crossOrigin so the canvas stays readable;
   * Supabase serves them with an open CORS header, which is what makes this
   * possible at all.
   */
  async #composeSheet(sheet, plan) {
    const columns = plan.grid?.columns || 2;
    const side = PREVIEW_SIDE_PX;
    const canvas = document.createElement("canvas");
    canvas.width = side;
    canvas.height = side;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, side, side);

    // The page, centred in the square. Height is the binding dimension: the
    // sheet is portrait and the square is not.
    const pageHeight = Math.round(side * PAGE.fill);
    const pageWidth = Math.round(pageHeight * PAGE.aspect);
    const pageX = Math.round((side - pageWidth) / 2);
    const pageY = Math.round((side - pageHeight) / 2);

    // A white page on a white background is only a page if it has an edge.
    ctx.save();
    ctx.shadowColor = "rgba(0, 0, 0, 0.15)";
    ctx.shadowBlur = side * 0.014;
    ctx.shadowOffsetY = side * 0.004;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(pageX, pageY, pageWidth, pageHeight);
    ctx.restore();

    const border = Math.max(1, Math.round(side / 700));
    ctx.strokeStyle = "#d8d8d8";
    ctx.lineWidth = border;
    ctx.strokeRect(
      pageX + border / 2,
      pageY + border / 2,
      pageWidth - border,
      pageHeight - border
    );

    const marginX = pageWidth * PAGE.marginX;
    const marginY = pageHeight * PAGE.marginY;
    const gutter = pageWidth * PAGE.gutter;
    const cell = (pageWidth - marginX * 2 - gutter * (columns - 1)) / columns;

    await Promise.all(
      sheet.stickers.map(
        (sticker) =>
          new Promise((resolve) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => {
              const row = sticker.row ?? Math.floor(sheet.stickers.indexOf(sticker) / columns);
              const column = sticker.column ?? sheet.stickers.indexOf(sticker) % columns;

              // The drawing, not the square it was drawn in: the empty margin
              // around a badge is taken out so the sticker is 2.25in on its
              // longest side, exactly as the print file does it.
              const box = contentBoxOf(image);
              const sx = box.x * image.width;
              const sy = box.y * image.height;
              const sw = box.width * image.width;
              const sh = box.height * image.height;

              // Scaled by the longest side, so nothing is cropped: a tall badge
              // gets a full-height sticker and keeps its width.
              const scale = cell / Math.max(sw, sh);
              const w = sw * scale;
              const h = sh * scale;
              ctx.drawImage(
                image,
                sx,
                sy,
                sw,
                sh,
                pageX + marginX + column * (cell + gutter) + (cell - w) / 2,
                pageY + marginY + row * (cell + gutter) + (cell - h) / 2,
                w,
                h
              );
              resolve();
            };
            // A badge that will not load leaves a gap rather than failing the
            // whole sheet; `missingArtwork` is what guards against that, and it
            // has already run.
            image.onerror = () => resolve();
            image.src = sticker.preview;
          })
      )
    );

    return canvas.toDataURL("image/jpeg", 0.92);
  }

  #escape(value) {
    const node = document.createElement("span");
    node.textContent = value;
    return node.innerHTML;
  }

  /**
   * One line per sheet, added through the cart API.
   *
   * The form cannot post this: it carries a single set of properties, and each
   * sheet needs its own print file. So the submit is taken over entirely.
   */
  async handleSubmit(event) {
    event.preventDefault();
    if (!this.plan) return;

    const variantId = this.productForm.querySelector(".product-variant-id")?.value;
    if (!variantId) {
      this.setBuyable(false, "This product is not set up correctly.");
      return;
    }

    this.setBuyable(false, "Making your sheets…");

    try {
      // Rendered before the sale, not after it. Each fetch warms the backend's
      // print-file cache, so Printful's download is served from there rather
      // than racing a render on a busy host.
      await Promise.all(
        this.plan.sheets.map((sheet) => this.#warm(sheet.printFile))
      );
    } catch (err) {
      console.error("[sticker-sheet] a sheet would not render.", err);
      this.setBuyable(
        true,
        "We had trouble preparing your sheets. Please try adding again."
      );
      return;
    }

    const items = this.plan.sheets.map((sheet) => ({
      id: Number(variantId),
      quantity: 1,
      properties: {
        _printfile_url: sheet.printFile,
        "Ship and Sail Date": this.#summary(),
        Sheet:
          this.plan.sheetCount === 1
            ? "1 of 1"
            : `${sheet.index} of ${this.plan.sheetCount}`,
      },
    }));

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) throw new Error(`cart/add failed: ${response.status}`);

      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      window.location.href = `${window.Shopify.routes.root}cart`;
    } catch (err) {
      console.error("[sticker-sheet] could not add to cart.", err);
      this.setBuyable(true, "We could not add that to your cart. Please try again.");
    }
  }

  /** Fetch a print file so it exists and is cached before anybody pays. */
  async #warm(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SHEET_RENDER_TIMEOUT_MS);
    try {
      const response = await fetch(url, {
        signal: controller.signal,
        cache: "no-store",
      });
      if (!response.ok) throw new Error(`${url} answered ${response.status}`);
      // Drain it: the point is that the render finished, not the bytes.
      await response.arrayBuffer();
    } finally {
      clearTimeout(timer);
    }
  }

  #summary() {
    const ship = this.shipSelect.options[this.shipSelect.selectedIndex]?.text;
    const date =
      this.saildateSelect.options[this.saildateSelect.selectedIndex]?.text;
    return `${ship} — ${date}`;
  }
}

customElements.define("variant-picker-sticker-sheet", VariantPickerStickerSheet);
